import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache } from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"
import { InteractionRequiredAuthError } from "@azure/msal-browser"
import { apiConfig } from "../authConfig"
import { msalInstance } from "../index"
import { store } from "./store"

const httpLink = createHttpLink({
  uri: process.env.NODE_ENV !== "production" ? "http://localhost:8080/query" : "/query",
  credentials: "include",
  fetchOptions: {
    timeout: 60000, // 60 second timeout for slow customer queries
  },
})

// Error link to capture GraphQL and network errors and expose them via context
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors && graphQLErrors.length > 0) {
    console.error("[Apollo errorLink] GraphQL errors:", graphQLErrors)
    operation.setContext(({ responseErrors = [] }) => ({
      responseErrors: [...responseErrors, ...graphQLErrors],
    }))
  }
  if (networkError) {
    console.error("[Apollo errorLink] Network error:", networkError)
    operation.setContext({ networkError })
  }
})

const authLink = setContext(async (_, { headers }) => {
  // Get the current state from the store directly
  const state = store.getState()
  const account = state.authenticatedUser
  console.log("apolloClient set context account", account)

  if (account) {
    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: apiConfig.scopes,
        account: account,
      })

      return {
        headers: {
          ...headers,
          Authorization: `Bearer ${response.accessToken}`,
          ...(process.env.NODE_ENV !== "production" && process.env.REACT_APP_DEV_SECRET
            ? { "X-Dev-Secret": process.env.REACT_APP_DEV_SECRET }
            : {}),
        },
      }
    } catch (error) {
      console.error("[Apollo authLink] Silent token acquisition error:", error)
      if (error instanceof InteractionRequiredAuthError) {
        console.warn(
          "[Apollo authLink] Silent token acquisition failed (InteractionRequiredAuthError). Attempting interactive redirect."
        )
        try {
          await msalInstance.acquireTokenRedirect({
            scopes: apiConfig.scopes,
            account: account,
          })
          console.log(
            "[Apollo authLink] acquireTokenRedirect initiated. This execution path should ideally not log further as page redirects."
          )
          return { headers }
        } catch (redirectError) {
          console.error(
            "[Apollo authLink] Interactive token acquisition (redirect) error:",
            redirectError
          )
          return { headers }
        }
      } else {
        console.warn(
          "[Apollo authLink] Non-interaction error during token acquisition. Returning original headers."
        )
        return { headers }
      }
    }
  }
  console.log("[Apollo authLink] No active account found. Returning original headers.")
  if (process.env.NODE_ENV !== "production" && process.env.REACT_APP_DEV_SECRET) {
    return {
      headers: {
        ...headers,
        "X-Dev-Secret": process.env.REACT_APP_DEV_SECRET,
      },
    }
  }
  return { headers }
})

// Custom link to remove __typename from variables
const cleanTypenameLink = new ApolloLink((operation, forward) => {
  // Deep clean function to remove __typename from any nested object
  const omitTypename = (key: string, value: any): any => {
    if (key === "__typename") {
      console.log("[Apollo] Removing __typename:", value)
      return undefined
    }
    return value
  }

  // Clean the variables by converting to JSON and back
  if (operation.variables) {
    const cleaned = JSON.parse(JSON.stringify(operation.variables, omitTypename))
    operation.variables = cleaned
  }

  return forward(operation)
})

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, cleanTypenameLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      User: { keyFields: ["id"] },
      Project: { keyFields: ["id"] },
      SalesQuote: { keyFields: ["id"] },
      Employee: { keyFields: ["id"] },
      // Device data groups are embedded per device and reuse the same IDs as their templates.
      // Disable normalized caching so values from template queries do not overwrite project-specific data.
      DeviceDataGroup: { keyFields: false },
      DeviceData: { keyFields: false },
    },
  }),
  queryDeduplication: true,
  defaultOptions: {
    query: {
      errorPolicy: "all",
      fetchPolicy: "cache-first",
    },
    mutate: {
      errorPolicy: "all",
    },
  },
})
export const backendPDFUrl =
  process.env.NODE_ENV !== "production"
    ? "http://localhost:8080/api/pdf/"
    : `${window.location.origin}/api/pdf/`
